import type { CameraKey } from './cameraPresets'

/**
 * Notices that the camera's USB cable was physically pulled, at the moment it happens.
 *
 * Until now nothing in the renderer listened for this: there was no
 * `navigator.usb.addEventListener('disconnect')` anywhere in the app. An unplug was
 * therefore discovered only when some later operation happened to fail — and in the
 * meantime every guard in the engine still believed the camera was connected
 * (`_isDisconnected` is set only by an explicit software `disconnect()`), so previews,
 * health probes and captures kept firing USB ops at a device that was no longer there.
 * That window is precisely where a half-torn-down session gets reused and wedges.
 *
 * The handler deliberately performs NO USB I/O. The device is gone; calling
 * `gp_camera_exit` or `releaseInterface` on it would issue round-trips that can never
 * be answered, which is the very thing that blocks the renderer thread. It only drops
 * local state, so the next connect starts from a clean slate via the existing
 * stale-session recovery path.
 *
 * It also stands down while a key is mid-reconnect. `resetUsbDeviceBestEffort` calls
 * `device.reset()`, which the engine itself describes as "the software equivalent of
 * replugging" — a bus reset that can surface to the page as a disconnect. Acting on that
 * would wipe `_claimedUsbDevice` and set `_isDisconnected` underneath
 * `connectClearingStaleSession` while its retry is still running, breaking the very
 * self-heal that lets a relaunch reconnect with no cable touch. If a genuine unplug
 * happens during a reconnect we simply miss it and fall back to the old behaviour, which
 * is the safe direction to fail in.
 *
 * Matching is conservative on purpose. A device is treated as ours only when it is the
 * same object we claimed, or when vendor/product/serial all match and a serial is
 * actually present. If we cannot be certain, we do nothing and fall back to the old
 * behaviour — a false "camera unplugged" on a booth whose camera is fine would be worse
 * than discovering the unplug a few seconds later.
 */

export const CAMERA_KEYS: readonly CameraKey[] = ['primary', 'secondary']

type ClaimedDevices = Record<CameraKey, USBDevice | null>

/** Is `candidate` the device this key claimed? */
export function isSameUsbDevice(claimed: USBDevice | null, candidate: USBDevice): boolean {
  if (!claimed) return false
  // Same object: WebUSB hands back one USBDevice per physical device per context, so
  // this is the normal, exact case.
  if (claimed === candidate) return true
  // Identity can be lost across a re-enumeration. A serial number makes the body
  // uniquely identifiable; without one, vendor+product would also match a second body
  // of the same model, so we decline to guess.
  const serial = claimed.serialNumber
  if (!serial || !candidate.serialNumber) return false
  return (
    claimed.vendorId === candidate.vendorId &&
    claimed.productId === candidate.productId &&
    serial === candidate.serialNumber
  )
}

/** Which camera keys were using the device that just went away. */
export function keysForUnpluggedDevice(device: USBDevice, claimed: ClaimedDevices): CameraKey[] {
  return CAMERA_KEYS.filter((key) => isSameUsbDevice(claimed[key], device))
}

export interface UsbUnplugWatcherOptions {
  /** Live view of what each key currently claims — read at event time, never cached. */
  getClaimed: () => ClaimedDevices
  /** Drop local state for this key. Must not perform USB I/O. */
  onUnplugged: (cameraKey: CameraKey, device: USBDevice) => void
  /**
   * True while this key is connecting or self-healing. Our own `device.reset()` can
   * look like a disconnect, so we must not tear down state under an in-flight retry.
   */
  isBusyReconnecting?: (cameraKey: CameraKey) => boolean
  /** Injected for tests; defaults to `navigator.usb`. */
  usb?: Pick<USB, 'addEventListener' | 'removeEventListener'>
  log?: (message: string) => void
}

/** Start watching. Returns a teardown function. */
export function installUsbUnplugWatcher(options: UsbUnplugWatcherOptions): () => void {
  const usb =
    options.usb ?? (typeof navigator !== 'undefined' ? (navigator.usb as USB | undefined) : undefined)
  // Non-Chromium or a test environment: nothing to watch, and that must not throw.
  if (!usb || typeof usb.addEventListener !== 'function') return (): void => {}

  const log = options.log ?? ((m: string): void => console.warn(m))

  const handler = (event: Event): void => {
    const device = (event as USBConnectionEvent).device
    if (!device) return
    const keys = keysForUnpluggedDevice(device, options.getClaimed())
    if (keys.length === 0) return // some other USB device — not our business
    for (const cameraKey of keys) {
      if (options.isBusyReconnecting?.(cameraKey)) {
        // Almost certainly our own bus reset, not a hand on the cable.
        log(`[USB] ${cameraKey} disconnect arrived while reconnecting — ignoring (likely our own reset)`)
        continue
      }
      log(
        `[USB] ${cameraKey} camera was unplugged (${device.productName ?? 'unknown'}) — dropping its session`
      )
      try {
        options.onUnplugged(cameraKey, device)
      } catch (err) {
        // A booth must never be left in a worse state because the cleanup threw.
        console.error(`[USB] unplug cleanup failed for ${cameraKey}:`, err)
      }
    }
  }

  usb.addEventListener('disconnect', handler)
  return (): void => usb.removeEventListener('disconnect', handler)
}
